import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import { styledButtonIcons } from '@mlb/react-patterns/components/TextInput/textInput.style';
import ButtonPattern from '@mlb/react-patterns/components/Button/button.tsx';
import { styledListWrapper, styledListItem } from '@mlb/react-patterns/components/TypeAhead/typeAhead.style';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';

import useSearchQueryParam from 'bdvp-advanced-search/src/hooks/store/useSearchQueryParam';
import { QUERY_TYPE } from 'bdvp-advanced-search/src/utils/constants';
import TypeaheadBase from 'bdvp-advanced-search/src/components/TypeaheadBase';
import {
    Link,
    ListItemTextContainer,
    ListItemTeamAttrContainer,
} from 'bdvp-advanced-search/src/components/TypeaheadBase/TypeaheadBase.styles';
import {
    useMedia,
    useDidUpdate,
    useSuggestions,
} from 'bdvp-common/src/hooks';
import { useDictionary, useMediaConfig } from 'bdvp-common/src/hooks/config';
import { QUERY_PARAMS, SEARCH_ROUTE } from 'bdvp-common/src/utils/constants';
import { Tracking } from 'bdvp-tracking';
import {
    SEARCHES_KEY,
    SUGGESTION_TYPES,
} from 'bdvp-common/src/utils/typeaheadConstants';

const Wrapper = styled.div`
    display: flex;
    position: relative;
    width: 100%;
    gap: 8px;
`;

const SUGGESTION_ITEM_FONT_SIZE = {
    xs: '14px',
    md: '16px',
};

const Typeahead = styled(TypeaheadBase)`
    width: 100%;

    input[type=text] {
        color: ${COLORS.mlbBlack};
        background: ${COLORS.mlbWhite};
        font-size: 14px;
        padding-left: 10px;
        padding-right: ${({ value }) => value !== '' ? '40px' : '8px'};
        height: 36px;
        border: solid 3px ${COLORS.transparent};

        ${media.mdmin`
            font-size: 16px;
            padding-left: 14px;
            padding-right: 40px;
        `}

        ::placeholder {
            color: ${COLORS.mlbGrey7};
        }

        &:focus {
            border: solid 3px #E6E6E6;
        }
    }

    ${styledButtonIcons} {
        top: 0;
        right: 0;
        height: 100%;
        width: 40px;
        margin: 0;
        padding: 0;
        background: ${COLORS.transparent};

        svg {
            margin: 0 auto;
        }
    }

    ${styledListWrapper} {
        width: ${({ theme }) => `calc(100vw - ${parseFloat(theme.pageTemplate.paddingLeftRight.xs) * 2}px)`};
        padding: 2px 10px 9px;
        background-color: ${COLORS.mlbWhite};
        color: ${COLORS.mlbGrey8};
        border: 3px solid #E6E6E6;
        box-shadow: -4px 8px 14px 5px rgba(0, 0, 0, 0.3);
        ${media.mdmin`
            width: ${({ theme }) => `calc(100vw * 0.75 - (${ 40 + parseFloat(theme.pageTemplate.paddingLeftRight.md)}px))`};
            padding: 2px 14px 10px;
        `}
        ${media.lgmin`
            width: 125%;
        `}
        ${media.xlmin`
            width: 100%;
        `}
        ${styledListItem} {
            padding: 7px 0;
            background-color: ${COLORS.mlbWhite};
            color: ${COLORS.mlbGrey8};
            font-size: ${SUGGESTION_ITEM_FONT_SIZE.xs};
            line-height: ${SUGGESTION_ITEM_FONT_SIZE.xs};
            font-weight: 400;
            ${ListItemTextContainer},
            ${Link},
            ${ListItemTeamAttrContainer} {
                color: ${COLORS.mlbGrey8};
                font-size: ${SUGGESTION_ITEM_FONT_SIZE.xs};
                line-height: ${SUGGESTION_ITEM_FONT_SIZE.xs};
                font-weight: 400;
                ${media.mdmin`
                    font-size: ${SUGGESTION_ITEM_FONT_SIZE.md};
                    line-height: ${SUGGESTION_ITEM_FONT_SIZE.md};
                `}
            }
            ${ListItemTextContainer} {
                margin: 0;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
            &[data-type="label"] {
                color: ${COLORS.libertyBlue};
                font-size: 16px;
                line-height: 16px;
                padding: 9px 0 7px;
                font-weight: 700;
                cursor: default;
                text-transform: none;
                &:hover {
                    color: ${COLORS.libertyBlue};
                }
                ${media.mdmin`
                    font-size: 20px;
                    line-height: 20px;
                    padding: 12px 0 8px;
                `}
            }
            &:hover, &.active {
                color: ${COLORS.linkBlue};
                ${ListItemTextContainer},
                ${Link},
                ${ListItemTeamAttrContainer} {
                    color: ${COLORS.linkBlue};
                }
            }
            ${media.mdmin`
                padding: 8px 0;
                font-size: ${SUGGESTION_ITEM_FONT_SIZE.md};
                line-height: ${SUGGESTION_ITEM_FONT_SIZE.md};
            `}
        }
    }
`;

const Button = styled(ButtonPattern)`
    position: absolute;
    height: 37px;
    right: 0px;
    top: 0px;
`;

const CancelButton = styled(ButtonPattern)`
    color: ${COLORS.mlbWhite};
`;

const SearchInput = (props) => {

    const {
        setShowFreeTextInput,
        placeholder,
        suggestedSearches,
        filterButton,
    } = props;

    const defaultSuggestions = useMemo(() => suggestedSearches.map((suggestion) => ({
        type: SUGGESTION_TYPES[SEARCHES_KEY],
        name: suggestion,
    })), [suggestedSearches]);

    const { suggestionKeys } = useMediaConfig();

    const {
        searchQuery,
        queryType,
    } = useSearchQueryParam();

    const [
        searchValue,
        setSearchValue,
    ] = useState(
        queryType === QUERY_TYPE.freetext
            ? (searchQuery ?? '').trim()
            : ''
    );

    const [
        suggestionsValue,
        setSuggestionsValue,
    ] = useState('');

    const [
        showDefaultSuggestions,
        setShowDefaultSuggestions,
    ] = useState(false);

    const searchInputRef = useRef(null);

    const isMobile = useMedia([`(min-width: ${BREAKPOINTS.md})`], [false], true);
    const {
        free_text_search = 'Free text search',
        cancel = 'Cancel',
    } = useDictionary();
    const history = useHistory();
    const {
        suggestions,
        clearSuggestions,
    } = useSuggestions({
        value: suggestionsValue,
        displayCount: 5,
        suggestionKeys,
    });

    useEffect(() => {

        let unsubscribeFromHistory = () => {};

        unsubscribeFromHistory = history.listen((newLocation) => {

            if (newLocation.pathname !== SEARCH_ROUTE) {

                clearSuggestions();
                setSuggestionsValue('');
                setSearchValue('');
                if (isMobile) {

                    setShowFreeTextInput(false);

                }

            }

        });

        return () => {

            unsubscribeFromHistory();

        };

    }, [
        history,
        isMobile,
        setShowFreeTextInput,
        clearSuggestions,
    ]);

    useDidUpdate(() => {

        if(queryType === QUERY_TYPE.freetext || !searchQuery) {

            setSearchValue(searchQuery ?? '');

        }

    }, [
        searchQuery,
        setSearchValue,
        queryType,
    ]);

    const executeAndTrackQuery = useCallback((searchText = '', wasClicked = true) => {

        const trimmedSearchText = searchText.trim();
        if(!trimmedSearchText || trimmedSearchText === searchQuery) {

            return;

        }

        Tracking.trackFreeTextSearchClickEnterQuery(trimmedSearchText, wasClicked);

        const oldUrlParams = new URLSearchParams(history.location.search);
        const urlParams = new URLSearchParams();
        urlParams.set(QUERY_PARAMS.query, trimmedSearchText);
        urlParams.set(QUERY_PARAMS.queryType, QUERY_TYPE.freetext);
        if(oldUrlParams.has(QUERY_PARAMS.openFilters)) {

            urlParams.set(QUERY_PARAMS.openFilters, oldUrlParams.get(QUERY_PARAMS.openFilters));
        
        }
        history.push({
            pathname: SEARCH_ROUTE,
            search: urlParams.toString(),
        });

        searchInputRef.current.blur();
        clearSuggestions();

    }, [
        history,
        searchInputRef,
        searchQuery,
        clearSuggestions,
    ]);

    const onChangeHandler = (event) => {

        const inputValue = event.target.value;
        const inputEmpty = inputValue === '';

        if (inputEmpty) {

            clearSuggestions();

        }

        setShowDefaultSuggestions(inputEmpty);
        setSuggestionsValue(inputValue);
        setSearchValue(inputValue);

    };

    const onFocusHandler = () => {

        if (!isMobile) {

            Tracking.trackFreeTextSearchFocus();

        }

        if (searchValue === '') {

            setShowDefaultSuggestions(true);

        }

    };

    const onClearInputHandler = (event, shouldResetValue) => {

        clearSuggestions();

        if(shouldResetValue) {

            setSearchValue('');
            Tracking.trackFreeTextSearchReset();
            setShowDefaultSuggestions(true);
            return;

        }

        // This is needed if you click outside of the search input
        if(searchValue === '') {

            setShowDefaultSuggestions(false);

        }

    };

    const onItemClickHandler = (event, info) => {

        if (showDefaultSuggestions && defaultSuggestions[info.index]) {

            const { name: suggestedSearch } = defaultSuggestions[info.index];
            Tracking.trackFreeTextSuggestedSearch(info.index, suggestedSearch);
            setSearchValue(suggestedSearch);
            executeAndTrackQuery(suggestedSearch);
            setShowDefaultSuggestions(false);
            return;

        }

        const { name } = suggestions[info.index];

        clearSuggestions();
        setSuggestionsValue('');
        setSearchValue(name);

    };

    return (
        <Wrapper>
            <Typeahead
                suggestions={showDefaultSuggestions ? defaultSuggestions : suggestions}
                value={searchValue}
                placeholder={placeholder}
                ariaLabel={free_text_search}
                shouldShowCancelButton={isMobile}
                inputRef={searchInputRef}
                shouldShowLabels={true}
                shouldShowImages={false}
                shouldClearValueOnSelect={false}
                onChange={onChangeHandler}
                onFocus={onFocusHandler}
                onItemClick={onItemClickHandler}
                onClearInput={onClearInputHandler}
                onKeyPress={(event) => {

                    if (event.key === 'Enter') {

                        executeAndTrackQuery(searchValue, false);

                    }

                }}
                additionalProps={
                    {
                        enterkeyhint: 'search',
                        autoFocus: isMobile,
                    }
                }
            />
            {isMobile
                ? (
                    <>
                        {filterButton}
                        <CancelButton
                            label={cancel}
                            size={'md'}
                            shape={'hollow-borderless'}
                            styleName={'link-white'}
                            handlers={{
                                onClick: () => {

                                    Tracking.trackFreeTextSearchCancel();
                                    setShowFreeTextInput(false);

                                },
                            }}
                        />
                    </>
                )
                : (
                    <Button
                        disabled={!!!searchValue}
                        iconName={'search'}
                        iconColor={COLORS.mlbGrey7}
                        hasHover={false}
                        size={'lg'}
                        shape={'hollow-borderless'}
                        styleName={'default'}
                        handlers={
                            {
                                onClick: () => {

                                    executeAndTrackQuery(searchValue);

                                },
                            }
                        }
                    />
                )
            }
        </Wrapper>
    );

};

SearchInput.propTypes = {
    placeholder: PropTypes.string.isRequired,
    setShowFreeTextInput: PropTypes.func,
    suggestedSearches: PropTypes.array,
};

SearchInput.defaultProps = {
    setShowFreeTextInput: () => {},
    suggestedSearches: [],
};

export default SearchInput;
